import { createColumnHelper } from '@tanstack/react-table';
import { Counter, EnterpriseScopeCounterDiscriminator } from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Tag } from '@optimus-web/core';
import { compactArray } from '@mews/fp';
import { UnicodeEntity } from '@mews/enums';
import { getNumberFormatFromCreditNoteCounter } from '../utils';
import { CounterRowActions } from './CounterRowActions';

const helper = createColumnHelper<Counter>();

const helperName = helper.accessor('name', {
    header: () => <LocalizedText textKey={TextKey.CounterName} />,
});

const helperDefault = helper.accessor('isDefault', {
    header: () => <LocalizedText textKey={TextKey.Default} />,
    cell: (props) => {
        const value = props.getValue();
        if (value) {
            return (
                <Tag variant="success">
                    <LocalizedText textKey={TextKey.Default} />
                </Tag>
            );
        }
        return (
            <Tag>
                <LocalizedText textKey={TextKey.Enabled} />
            </Tag>
        );
    },
});
const helperActions = helper.accessor('id', {
    id: 'actions',
    header: '',
    meta: { align: 'end' },
    cell: ({ getValue }) => <CounterRowActions counterId={getValue()} />,
});

const typeToTextKeyMap = {
    [EnterpriseScopeCounterDiscriminator.BillCounter]: TextKey.Bill,
    [EnterpriseScopeCounterDiscriminator.ProformaCounter]: TextKey.Proforma,
    [EnterpriseScopeCounterDiscriminator.BillPreviewCounter]: TextKey.BillPreview,
    [EnterpriseScopeCounterDiscriminator.AccountingCounter]: TextKey.Accounting,
    [EnterpriseScopeCounterDiscriminator.CorrectionBillCounter]: TextKey.Correction,
    [EnterpriseScopeCounterDiscriminator.PaymentConfirmationBillCounter]: TextKey.PaymentConfirmation,
    [EnterpriseScopeCounterDiscriminator.AvailabilityBlockCounter]: TextKey.AvailabilityBlock,
    [EnterpriseScopeCounterDiscriminator.FiscalCounter]: TextKey.Fiscal,
    [EnterpriseScopeCounterDiscriminator.RegistrationCardCounter]: TextKey.RegistrationCard,
    [EnterpriseScopeCounterDiscriminator.ServiceOrderCounter]: TextKey.ServiceOrder,
    [EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter]: TextKey.CreditNote,
};

export const creditNoteCounterColumnMapping = [
    helperName,
    helper.accessor('data', {
        id: 'type',
        header: () => <LocalizedText textKey={TextKey.CounterUsage} />,
        cell: (props) => {
            const value = props.getValue();

            if (value?.discriminator !== EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter) return null;

            const billType = value.value.applicableBillType;

            const items = compactArray([
                billType.applyToInvoice ? <LocalizedText textKey={TextKey.InvoicePlural} /> : undefined,
                billType.applyToBill ? <LocalizedText textKey={TextKey.BillPlural} /> : undefined,
            ]);

            return (
                <>
                    {items.map((item, index) =>
                        index === items.length - 1 ? (
                            item
                        ) : (
                            <>
                                {item}
                                {UnicodeEntity.Comma}
                                {UnicodeEntity.NoBreakSpace}
                            </>
                        )
                    )}
                </>
            );
        },
    }),
    helper.accessor('value', {
        header: () => <LocalizedText textKey={TextKey.Value} />,
    }),
    helper.accessor('data', {
        id: 'numberFormat',
        header: () => <LocalizedText textKey={TextKey.NumberFormat} />,
        cell: (props) => getNumberFormatFromCreditNoteCounter(props.getValue()) || UnicodeEntity.Dash,
    }),
    helperDefault,
    helperActions,
];

export const noteCounterColumnMapping = [
    helperName,
    helper.accessor('data.discriminator', {
        header: () => <LocalizedText textKey={TextKey.Type} />,
        cell: (props) => <LocalizedText textKey={typeToTextKeyMap[props.getValue()]} />,
    }),
    helper.accessor('value', {
        header: () => <LocalizedText textKey={TextKey.Value} />,
    }),
    helper.accessor('format', {
        header: () => <LocalizedText textKey={TextKey.NumberFormat} />,
        cell: (props) => props.getValue() || UnicodeEntity.Dash,
    }),
    helperDefault,
    helperActions,
];
